/*
 * Gift with purchase — cart prompt + chooser modal.
 *
 * Markup: snippets/gift-with-purchase-claim.liquid (the prompt, re-rendered with the cart),
 * snippets/gift-with-purchase-modal.liquid (the modal shell, rendered once) and
 * sections/gift-with-purchase.liquid (the gift options, fetched when the modal opens).
 *
 * The theme never prices anything. The chosen gift goes into the cart as an ordinary line
 * with the hidden `_gift_with_purchase` property; a Shopify automatic discount (Buy X get Y
 * on the same two collections, one gift per qualifying item) makes it free. The one rule
 * this file enforces: A CLAIMED GIFT IS NEVER SOLD. After adding one we read the cart back,
 * and whenever the cart re-renders with a claimed gift that is being charged
 * (data-state="charged": its qualifying item left the cart, the discount is off, or a
 * discount code that cannot be combined with it saves the shopper more) the charged unit
 * is taken out again.
 *
 * An offer (the modal's data-percent below 100, e.g. a scarf at 50% with the order) works the
 * same way with one difference: the claimed line is meant to cost something, so "charged"
 * means charged IN FULL — no discount on it. An offer is never sold at full price.
 *
 * The modal opens by itself when the shopper sees the cart (drawer opened, cart re-rendered
 * after an add, or the cart page loaded) with an unclaimed gift AND something in it they have
 * not been prompted next to yet — once per product + size (variant) in a browser session.
 * Adding another product, or another size of the same one, brings the chooser up again;
 * adding the same size again does not. In between, the prompt inside the cart is the way in.
 * A prompt with data-auto-open="false" never opens the chooser by itself: only its button does.
 */
(() => {
  if (customElements.get('gift-with-purchase-modal')) return;

  const PROPERTY = '_gift_with_purchase';
  const OPTION_NAME = 'gift-with-purchase';
  // the variants the chooser has already opened by itself for, comma separated
  const PROMPTED_KEY = 'gift-with-purchase:prompted-variants';
  // The drawer slides in over 450ms and takes focus when it lands; prompt after that.
  const PROMPT_DELAY = 700;

  // sessionStorage, mirrored in memory so that this page still remembers when storage is blocked
  const remembered = {};
  const session = {
    get(key) {
      try {
        const value = window.sessionStorage.getItem(key);
        if (value !== null) return value;
      } catch (error) {
        /* storage blocked */
      }
      return key in remembered ? remembered[key] : null;
    },
    set(key, value) {
      remembered[key] = value;
      try {
        window.sessionStorage.setItem(key, value);
      } catch (error) {
        /* storage blocked: the prompt may show again on the next page, nothing else breaks */
      }
    }
  };

  const route = (name, fallback) => (window.routes && window.routes[name]) || fallback;
  const cartUrl = () => route('cart_url', '/cart');

  const getModal = () => document.querySelector('gift-with-purchase-modal');
  const getDrawer = () => document.querySelector('cart-drawer');

  const track = (event, data) => {
    try {
      (window.dataLayer = window.dataLayer || []).push(Object.assign({ event }, data));
    } catch (error) {
      /* analytics only */
    }
  };

  const postJSON = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });

  const getCart = () =>
    fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' } }).then(response => {
      if (!response.ok) throw new Error(`cart ${response.status}`);
      return response.json();
    });

  const isGiftLine = item => !!(item && item.properties && item.properties[PROPERTY]);
  // a free gift: charged as soon as it costs anything; an offer: charged when nothing came off it
  const isOffer = () => {
    const modal = getModal();
    return !!modal && Number(modal.dataset.percent) < 100;
  };
  const isCharged = item =>
    isGiftLine(item) && (isOffer() ? item.final_line_price >= item.original_line_price : item.final_line_price > 0);

  /* ---- keeping the cart on screen in step ---------------------------------------------- */

  // What the theme re-renders on a cart change (drawer body, cart counter and, on the cart
  // page, the cart itself) — the list its own cart.js keeps.
  const sectionsToRender = () => {
    const host = getDrawer() || document.querySelector('cart-items');
    return host && typeof host.getSectionsToRender === 'function' ? host.getSectionsToRender() || [] : [];
  };

  // Have Shopify render those sections in the same response as the cart change.
  const withSections = body => {
    const ids = sectionsToRender().map(section => section.section);
    return ids.length ? Object.assign({}, body, { sections: ids, sections_url: window.location.pathname }) : body;
  };

  /** Puts freshly rendered sections in place, the way the theme's cart.js does. */
  function applySections(rendered) {
    if (!rendered) return false;
    const parser = new DOMParser();
    let applied = false;
    sectionsToRender().forEach(section => {
      const html = rendered[section.section];
      const target = document.querySelector(section.selector) || document.querySelector(section.id);
      if (!html || !target) return;
      const source = parser.parseFromString(html, 'text/html').querySelector(section.selector);
      if (!source) return;
      target.innerHTML = source.innerHTML;
      applied = true;
    });
    if (applied) {
      if (typeof updateCartCounters === 'function') updateCartCounters();
      if (typeof updateFreeShipping === 'function') updateFreeShipping();
      if (typeof handleCartInputNames === 'function') handleCartInputNames();
    }
    return applied;
  }

  async function refreshCart() {
    const ids = sectionsToRender().map(section => section.section).join(',');
    if (!ids) return;
    const response = await fetch(`${cartUrl()}?sections=${ids}&sections_url=${window.location.pathname}`);
    if (!response.ok) throw new Error(`sections ${response.status}`);
    applySections(await response.json());
  }

  /** Sets a line's quantity (0 removes it); resolves with the cart after the change, sections included. */
  async function changeLine(key, quantity) {
    const response = await postJSON(`${route('cart_change_url', '/cart/change')}.js`, withSections({ id: key, quantity }));
    if (!response.ok) throw new Error(`change ${response.status}`);
    return response.json();
  }

  /* ---- a claimed gift that is being charged -------------------------------------------- */

  let settling = false;
  let settleFailures = 0;

  async function settleGifts(marker) {
    // two failed rounds in a row: stop, or every re-render would start another one
    if (settling || settleFailures >= 2) return;
    settling = true;
    const items = marker ? marker.closest('cart-drawer-items, cart-items') : null;
    if (items) items.classList.add('is-loading'); // the theme's own "cart is updating" veil

    let rendered = null;
    let changed = false;
    try {
      let cart = await getCart();
      // One unit per pass: each change shifts the discount (and re-keys the lines), so the
      // next pass works from the cart that change returned. The cap is a runaway guard only.
      for (let pass = 0; pass < 20; pass++) {
        const line = cart.items.find(isCharged);
        if (!line) break;
        cart = await changeLine(line.key, line.quantity - 1);
        rendered = cart.sections;
        changed = true;
      }
      settleFailures = 0;
    } catch (error) {
      settleFailures += 1;
    }

    try {
      if (changed && !applySections(rendered)) await refreshCart();
    } catch (error) {
      /* the next cart change re-renders it */
    } finally {
      if (items) items.classList.remove('is-loading');
      settling = false;
    }
  }

  /* ---- the automatic prompt ----------------------------------------------------------- */

  /** The "Claim your gift" prompt the shopper can see right now, if any. */
  function visibleClaim() {
    const claims = document.querySelectorAll('gift-with-purchase-claim[data-state="available"]');
    for (const claim of claims) {
      const drawer = claim.closest('cart-drawer');
      if (drawer && !drawer.classList.contains('is-visible')) continue;
      if (claim.querySelector('[data-gift-open]')) return claim;
    }
    return null;
  }

  function visibleClaimButton() {
    const claim = visibleClaim();
    return claim ? claim.querySelector('[data-gift-open]') : null;
  }

  const idList = value => (value || '').split(',').filter(Boolean);

  let promptTimer = null;

  // Runs after every cart render and every opening of the drawer. The chooser opens by itself
  // only when the cart on screen holds a variant it has not opened for yet: another product
  // or another size prompts again, the same size added again (or a changed quantity) does not.
  function schedulePrompt() {
    if (window.Shopify && window.Shopify.designMode) return;
    window.clearTimeout(promptTimer);
    promptTimer = window.setTimeout(() => {
      const modal = getModal();
      const claim = visibleClaim();
      if (!modal || !claim || claim.dataset.autoOpen === 'false') return;

      // data-variants: what is in the cart, claimed gifts aside. A prompt rendered without
      // the list counts as one unknown item, i.e. the chooser opens once per session.
      const inCart = idList(claim.dataset.variants);
      if (!inCart.length) inCart.push('cart');
      const prompted = idList(session.get(PROMPTED_KEY));
      if (inCart.every(id => prompted.includes(id))) return;

      session.set(PROMPTED_KEY, Array.from(new Set(prompted.concat(inCart))).join(','));
      // already open (the shopper used the button): they are looking at it, that counts
      if (!modal.isOpen) modal.open(claim.querySelector('[data-gift-open]'), { auto: true });
    }, PROMPT_DELAY);
  }

  function watchDrawer() {
    const drawer = getDrawer();
    if (!drawer || drawer.giftWithPurchaseWatched) return;
    drawer.giftWithPurchaseWatched = true;
    let wasVisible = drawer.classList.contains('is-visible');
    new MutationObserver(() => {
      const visible = drawer.classList.contains('is-visible');
      if (visible && !wasVisible) schedulePrompt();
      wasVisible = visible;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---- the prompt inside the cart ----------------------------------------------------- */

  class GiftWithPurchaseClaim extends HTMLElement {
    connectedCallback() {
      if (this.dataset.state === 'charged') {
        settleGifts(this);
        return;
      }
      if (this.dataset.state !== 'available') return;

      const button = this.querySelector('[data-gift-open]');
      const modal = getModal();
      if (!button || !modal) {
        this.hidden = true; // nothing to open: don't offer it
        return;
      }
      if (!this.bound) {
        this.bound = true;
        button.addEventListener('click', () => modal.open(button));
      }
      watchDrawer();
      schedulePrompt();
    }
  }

  /* ---- the chooser -------------------------------------------------------------------- */

  class GiftWithPurchaseModal extends HTMLElement {
    connectedCallback() {
      // Moved to <body> on first open, which runs connectedCallback again.
      if (this.initialised) return;
      this.initialised = true;

      this.dialog = this.querySelector('[data-gift-dialog]');
      this.body = this.querySelector('[data-gift-body]');
      this.confirmButton = this.querySelector('[data-gift-confirm]');
      this.errorEl = this.querySelector('[data-gift-error]');
      this.selection = this.querySelector('[data-gift-selection]');
      this.selectedName = this.querySelector('[data-gift-selected-name]');
      this.selectedPrice = this.querySelector('[data-gift-selected-price]');
      this.selectedOffer = this.querySelector('[data-gift-selected-offer]');
      this.loaded = false;
      this.busy = false;
      this.trigger = null;

      this.onKeydown = this.onKeydown.bind(this);
      this.onKeyup = this.onKeyup.bind(this);
      this.onFocusin = this.onFocusin.bind(this);

      this.querySelectorAll('[data-gift-close]').forEach(element => {
        element.addEventListener('click', () => this.close());
      });
      this.confirmButton.addEventListener('click', () => this.confirm());
      this.addEventListener('change', event => {
        if (event.target && event.target.name === OPTION_NAME) this.updateSelection();
      });
      this.addEventListener('click', event => {
        // The dark surround is this element itself (no overlay child: the theme hides empty <div>s).
        if (event.target === this) this.close();
        // The cart drawer closes on any document click outside its own panel, and this modal
        // sits outside it (it is moved to <body>): keep clicks made in here to ourselves.
        event.stopPropagation();
      });

      watchDrawer();
    }

    get isOpen() {
      return !this.hidden;
    }

    get checked() {
      return this.querySelector(`input[name="${OPTION_NAME}"]:checked`);
    }

    open(trigger, options = {}) {
      if (this.isOpen) return;
      this.trigger = trigger || null;

      // Portal to <body> so position: fixed is not trapped by the drawer's transform.
      if (this.parentElement !== document.body) document.body.appendChild(this);

      this.hidden = false;
      this.bodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      // The drawer's focus trap would fight ours while we are on top of it.
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('keyup', this.onKeyup);
      document.addEventListener('focusin', this.onFocusin);
      this.dialog.focus({ preventScroll: true });

      track('gift_with_purchase_open', { gift_prompt: options.auto ? 'auto' : 'button' });
      this.load();
    }

    close() {
      if (!this.isOpen || this.busy) return;
      this.hidden = true;
      document.body.style.overflow = this.bodyOverflow || '';
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('keyup', this.onKeyup);
      document.removeEventListener('focusin', this.onFocusin);
      this.clearError();
      this.restoreFocus();
    }

    /** Back to where the shopper was: the prompt's button, else the cart itself. */
    restoreFocus() {
      // The cart may have re-rendered while the modal was open: the opener can be gone.
      const opener = this.trigger && this.trigger.isConnected ? this.trigger : visibleClaimButton();
      this.trigger = null;

      const drawer = getDrawer();
      if (drawer && drawer.classList.contains('is-visible')) {
        const target = opener || drawer.querySelector('[role="dialog"]');
        if (typeof trapFocus === 'function') {
          trapFocus(drawer.firstElementChild, target); // hand the trap back to the drawer
        } else if (target) {
          target.focus({ preventScroll: true });
        }
        return;
      }

      let target = opener;
      if (!target) {
        target = document.getElementById('main-cart-items') || document.querySelector('cart-items');
        if (target && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      }
      if (target) target.focus({ preventScroll: true });
    }

    async load(force = false) {
      if (this.loaded && !force) return;
      this.body.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(this.dataset.sectionUrl, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`section ${response.status}`);
        const html = await response.text();
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-gift-options]');
        if (!source) throw new Error('no gift options');
        this.body.replaceChildren(document.importNode(source, true));
        this.loaded = true;
      } catch (error) {
        this.showError(this.dataset.error);
      } finally {
        this.body.removeAttribute('aria-busy');
        this.updateSelection();
      }
    }

    updateSelection() {
      const input = this.checked;
      this.confirmButton.disabled = !input || this.busy;
      this.selection.toggleAttribute('data-empty', !input);
      this.selectedName.textContent = input ? input.dataset.giftTitle || '' : '';
      this.selectedPrice.textContent = input ? input.dataset.giftPrice || '' : '';
      if (this.selectedOffer) this.selectedOffer.textContent = input ? input.dataset.giftOfferPrice || '' : '';
      if (input) this.clearError();
    }

    setBusy(busy) {
      this.busy = busy;
      this.classList.toggle('is-busy', busy);
      this.confirmButton.disabled = busy || !this.checked;
      if (busy) {
        this.confirmButton.setAttribute('aria-busy', 'true');
      } else {
        this.confirmButton.removeAttribute('aria-busy');
      }
      const fieldset = this.querySelector('[data-gift-fieldset]');
      if (fieldset) fieldset.disabled = busy;
    }

    showError(message) {
      this.errorEl.textContent = message || this.dataset.error || '';
      this.errorEl.hidden = false;
    }

    clearError() {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    async confirm() {
      const input = this.checked;
      if (!input || this.busy) return;
      this.clearError();
      this.setBusy(true);

      const variantId = Number(input.value);
      let message = '';
      let changed = false; // did the cart change at all
      let rendered = null; // the cart sections Shopify rendered with our last cart change
      try {
        const added = await postJSON(
          `${route('cart_add_url', '/cart/add')}.js`,
          withSections({ items: [{ id: variantId, quantity: 1, properties: { [PROPERTY]: 'true' } }] })
        );
        if (!added.ok) {
          // 422: sold out in the meantime — Shopify's own sentence says which and why
          const problem = await added.json().catch(() => ({}));
          message = problem.description || problem.message || this.dataset.error;
        } else {
          changed = true;
          rendered = (await added.json()).sections;
          const cart = await getCart();
          if (cart.items.some(isCharged)) {
            // Not free (an offer: not discounted): no discount behind it, or a discount code in
            // the cart saves the shopper more and cannot be combined with it. Take back the unit we just added (the same
            // colour claimed twice shares one line, so step its quantity down rather than drop it).
            const coded = Array.isArray(cart.discount_codes) && cart.discount_codes.some(code => code && code.applicable);
            message = coded ? this.dataset.errorCombination : this.dataset.errorUnavailable;
            // (Shopify splits a line when only part of it is discounted: prefer the charged part.)
            const mine = item => isGiftLine(item) && item.variant_id === variantId;
            const line = cart.items.find(item => mine(item) && isCharged(item)) || cart.items.find(mine);
            rendered = line ? (await changeLine(line.key, line.quantity - 1)).sections : null;
          }
        }
      } catch (error) {
        message = message || this.dataset.error;
        rendered = null;
      }

      if (changed) {
        try {
          if (!applySections(rendered)) await refreshCart();
        } catch (error) {
          window.location.reload();
          return;
        }
      }

      this.setBusy(false);
      if (message) {
        this.showError(message);
        track('gift_with_purchase_error', { gift_variant_id: input.value });
        if (!changed) this.load(true); // stock moved: show what is still there
        return;
      }

      track('gift_with_purchase_claim', { gift_variant_id: input.value, gift_title: input.dataset.giftTitle || '' });
      input.checked = false;
      this.updateSelection();
      this.close();
    }

    /* ---- keyboard ---- */

    focusable() {
      return Array.from(
        this.dialog.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')
      ).filter(element => element.offsetParent !== null);
    }

    onKeydown(event) {
      if (event.key !== 'Tab') return;
      const focusable = this.focusable();
      if (!focusable.length) {
        event.preventDefault();
        this.dialog.focus({ preventScroll: true });
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === this.dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // keyup, not keydown: closing moves focus back into the cart drawer, and the drawer
    // closes itself on an Escape keyup — on keydown the same key press would close both.
    onKeyup(event) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.close();
    }

    onFocusin(event) {
      if (this.contains(event.target)) return;
      this.dialog.focus({ preventScroll: true });
    }
  }

  customElements.define('gift-with-purchase-modal', GiftWithPurchaseModal);
  customElements.define('gift-with-purchase-claim', GiftWithPurchaseClaim);
})();
